import { diffArrays, diffWords } from "diff";

/**
 * Rapor bölümünün HTML'ini karşılaştırılabilir düz metne çevirir: paragraf ve satır sonları korunur,
 * &nbsp; ve fazla boşluklar tekilleşir. DOMParser betik çalıştırmaz; içerik yalnız metin olarak okunur.
 */
export function htmlToText(html)
{
    if (!html)
        return '';

    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    doc.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
    doc.querySelectorAll('p, div, li, tr, h1, h2, h3, h4, h5, h6').forEach(el => el.append('\n'));

    return (doc.body.textContent || '')
        .replace(/ /g, ' ')
        .replace(/[ \t]+/g, ' ')
        .replace(/ *\n */g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

/**
 * İki bölüm içeriğinin farkı.
 *   status: 'same'   → metin ve biçim aynı
 *           'format' → metin aynı, yalnız HTML (kalın, boyut vb.) değişmiş
 *           'changed'→ kelime düzeyinde fark var; parts diffWords çıktısı ({ value, added, removed })
 */
export function diffSection(olderHtml, newerHtml)
{
    const older = htmlToText(olderHtml);
    const newer = htmlToText(newerHtml);

    if (older === newer)
        return { status: (olderHtml || '') === (newerHtml || '') ? 'same' : 'format', text: newer, parts: [] };

    return { status: 'changed', text: newer, parts: diffWords(older, newer) };
}

// Bölüm HTML'inin metin düğümlerindeki kelimeler, belge sırasıyla ({ node, start, end, word }).
function wordTokens(body)
{
    const tokens = [];
    const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode())
    {
        for (const match of node.data.matchAll(/\S+/g))
            tokens.push({ node, start: match.index, end: match.index + match[0].length, word: match[0] });
    }
    return tokens;
}

/**
 * Yeni bölümün HTML'ini biçimi (kalın, boyut, renk, paragraflar) koruyarak kelime düzeyinde işaretler: eskide olmayan
 * kelimeler <ins class="rv-ins">, eskide olup yenide olmayanlar bulundukları yerde <del class="rv-del"> olarak eklenir.
 * Yalnız metin karşılaştırılır; yalnız biçimi değişen kelime işaretlenmez. Dönen HTML ReportHtml ile basılır.
 *   changed: metinde fark var mı
 */
export function diffSectionHtml(olderHtml, newerHtml)
{
    const parse = (html) => new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html').body;
    const older = wordTokens(parse(olderHtml)).map(x => x.word);
    const body = parse(newerHtml);
    const tokens = wordTokens(body);

    // Her yeni kelime için: eklendi mi, önüne hangi silinen metin gelecek; sona kalan silinenler ayrı.
    const added = new Array(tokens.length).fill(false);
    const removedBefore = new Array(tokens.length).fill(null);
    let removedAtEnd = null;
    let index = 0;
    let changed = false;
    for (const part of diffArrays(older, tokens.map(x => x.word)))
    {
        if (part.removed)
        {
            changed = true;
            const text = part.value.join(' ');
            if (index < tokens.length)
                removedBefore[index] = removedBefore[index] ? `${removedBefore[index]} ${text}` : text;
            else
                removedAtEnd = removedAtEnd ? `${removedAtEnd} ${text}` : text;
            continue;
        }
        if (part.added)
        {
            changed = true;
            for (let i = 0; i < part.count; i++)
                added[index + i] = true;
        }
        index += part.count;
    }

    if (!changed)
        return { changed, html: newerHtml || '' };

    const doc = body.ownerDocument;
    const element = (tag, className, text) => {
        const el = doc.createElement(tag);
        el.className = className;
        el.textContent = text;
        return el;
    };

    // Düğüm düğüm yeniden kurulur; aynı düğümdeki ardışık eklenen kelimeler (aradaki boşlukla) tek <ins> olur.
    let i = 0;
    while (i < tokens.length)
    {
        const node = tokens[i].node;
        const fragment = doc.createDocumentFragment();
        let cursor = 0;
        while (i < tokens.length && tokens[i].node === node)
        {
            const token = tokens[i];
            fragment.append(node.data.slice(cursor, token.start));
            if (removedBefore[i])
                fragment.append(element('del', 'rv-del', removedBefore[i]), ' ');

            if (added[i])
            {
                let end = token.end;
                while (i + 1 < tokens.length && tokens[i + 1].node === node && added[i + 1] && !removedBefore[i + 1])
                    end = tokens[++i].end;
                fragment.append(element('ins', 'rv-ins', node.data.slice(token.start, end)));
                cursor = end;
            }
            else
            {
                fragment.append(token.word);
                cursor = token.end;
            }
            i++;
        }
        fragment.append(node.data.slice(cursor));
        node.replaceWith(fragment);
    }

    // Sona kalan silinenler son kelimenin hemen arkasına (aynı satır ve biçim içinde); yeni bölüm boşsa tek paragraf.
    if (removedAtEnd)
    {
        const del = element('del', 'rv-del', removedAtEnd);
        const anchor = findLastTextPosition(body);
        if (anchor)
            anchor.after(' ', del);
        else
        {
            const paragraph = doc.createElement('p');
            paragraph.append(del);
            body.append(paragraph);
        }
    }

    return { changed, html: body.innerHTML };
}

// Gövdedeki son boş olmayan metin düğümü ya da işaret elemanı.
function findLastTextPosition(body)
{
    const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    let last = null;
    for (let node = walker.nextNode(); node; node = walker.nextNode())
    {
        if (node.data.trim())
            last = node;
    }
    if (!last)
        return null;
    // <ins>/<del> içindeki metinse elemanın kendisinin arkasına eklenir.
    const parent = last.parentElement;
    return parent && (parent.matches('ins.rv-ins, del.rv-del')) ? parent : last;
}
